import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { timesheetService } from '../services/TimesheetService';
import { sessionNoteService } from '../services/SessionNoteService';
import { userService } from '../services/UserService';
import { activityService } from '../services/ActivityService';
import {
  ActivityCategory,
  ActivityEntry,
  ACTIVITY_CATEGORIES,
  ACTIVITY_CATEGORY_LABELS,
} from '../models/ActivityCategory';
import { Timesheet, TimesheetStatus } from '../models/Timesheet';
import {
  Clock,
  DollarSign,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
} from 'lucide-react';

// Default the generate form to the last two weeks (a typical bi-weekly period).
const toISODate = (d: Date) => d.toISOString().slice(0, 10);
const defaultPeriodEnd = () => toISODate(new Date());
const defaultPeriodStart = () => {
  const d = new Date();
  d.setDate(d.getDate() - 13);
  return toISODate(d);
};

export const Timesheets: React.FC = () => {
  const { user } = useAuth();
  const { company } = useCompany();
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimesheet, setSelectedTimesheet] = useState<Timesheet | null>(null);
  const [periodStart, setPeriodStart] = useState<string>(defaultPeriodStart());
  const [periodEnd, setPeriodEnd] = useState<string>(defaultPeriodEnd());
  const [generating, setGenerating] = useState(false);
  const [generateMessage, setGenerateMessage] = useState<string | null>(null);

  // Admins/supervisors can generate for and view any RBT's timesheets. '' = self.
  const isManager = user?.role === 'admin' || user?.role === 'supervisor';
  const [rbts, setRbts] = useState<Array<{ id: string; name: string }>>([]);
  const [targetRbtId, setTargetRbtId] = useState<string>('');

  // Non-billable time (the logged-in person's own activity clock).
  const [openEntry, setOpenEntry] = useState<ActivityEntry | null>(null);
  const [activityEntries, setActivityEntries] = useState<ActivityEntry[]>([]);
  const [activityCategory, setActivityCategory] = useState<ActivityCategory>('documentation');
  const [activityMinutes, setActivityMinutes] = useState('');
  const [activityNotes, setActivityNotes] = useState('');
  const [activityBusy, setActivityBusy] = useState(false);
  const [activityMessage, setActivityMessage] = useState<string | null>(null);

  const loadActivity = async () => {
    if (!user) return;
    try {
      const [open, list] = await Promise.all([
        activityService.getOpenEntry(user.id),
        activityService.getByRbtAndPeriod(
          user.id,
          new Date(`${periodStart}T00:00:00`),
          new Date(`${periodEnd}T23:59:59`)
        ),
      ]);
      setOpenEntry(open);
      setActivityEntries(list);
    } catch (e) {
      console.error('Failed to load activity entries:', e);
    }
  };

  useEffect(() => {
    loadActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, periodStart, periodEnd]);

  const handleClockIn = async () => {
    if (!user) return;
    setActivityBusy(true);
    setActivityMessage(null);
    try {
      await activityService.clockIn(user.companyId, user.id, user.name || 'Employee', activityCategory, activityNotes || undefined);
      setActivityNotes('');
      await loadActivity();
    } catch (e) {
      console.error('clock in error:', e);
      setActivityMessage('Failed to clock in');
    } finally {
      setActivityBusy(false);
    }
  };

  const handleClockOut = async () => {
    if (!openEntry) return;
    setActivityBusy(true);
    setActivityMessage(null);
    try {
      await activityService.clockOut(openEntry.id, openEntry.startTime);
      await loadActivity();
    } catch (e) {
      console.error('clock out error:', e);
      setActivityMessage('Failed to clock out');
    } finally {
      setActivityBusy(false);
    }
  };

  const handleAddActivity = async () => {
    if (!user) return;
    const mins = parseInt(activityMinutes, 10);
    if (isNaN(mins) || mins <= 0) {
      setActivityMessage('Enter minutes greater than 0.');
      return;
    }
    setActivityBusy(true);
    setActivityMessage(null);
    try {
      await activityService.addManual(user.companyId, user.id, user.name || 'Employee', activityCategory, new Date(), mins, activityNotes || undefined);
      setActivityMinutes('');
      setActivityNotes('');
      await loadActivity();
      setActivityMessage('Activity logged.');
    } catch (e) {
      console.error('add activity error:', e);
      setActivityMessage('Failed to log activity');
    } finally {
      setActivityBusy(false);
    }
  };

  // The RBT the page is currently acting on (self by default).
  const resolveTarget = (): { id: string; name: string; role: string } => {
    if (!targetRbtId || targetRbtId === user?.id) {
      return { id: user?.id || '', name: user?.name || user?.email || 'Employee', role: user?.role || 'rbt' };
    }
    const r = rbts.find((x) => x.id === targetRbtId);
    return { id: targetRbtId, name: r?.name || 'RBT', role: 'rbt' };
  };

  useEffect(() => {
    loadTimesheets(resolveTarget().id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, targetRbtId]);

  // Load the company's RBTs for the target picker (managers only).
  useEffect(() => {
    if (!user || !isManager) return;
    userService
      .getUsersByCompany(user.companyId)
      .then((all) =>
        setRbts(all.filter((u) => u.role === 'rbt').map((u) => ({ id: u.id, name: u.name })))
      )
      .catch((e) => console.error('Failed to load RBTs:', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isManager]);

  const loadTimesheets = async (targetId?: string) => {
    const id = targetId || user?.id;
    if (!user || !id) {
      setLoading(false);
      return;
    }

    try {
      const userTimesheets = await timesheetService.getTimesheetsByUser(id);
      setTimesheets(userTimesheets);
      setError(null);
    } catch (err) {
      console.error('Error loading timesheets:', err);
      setError('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };

  // Generate a draft for one RBT over the current period. Returns a status string.
  const generateForRbt = async (
    rbtId: string,
    rbtName: string,
    rbtRole: string,
    start: Date,
    end: Date
  ): Promise<string> => {
    const allNotes = await sessionNoteService.getSessionNotesByRBT(rbtId, company?.id || user?.companyId || '');
    const notesInPeriod = allNotes.filter((note) => {
      const s = note.timestamps?.sessionStart;
      if (!s) return false;
      const startMillis = s.toMillis();
      return startMillis >= start.getTime() && startMillis <= end.getTime();
    });
    if (notesInPeriod.length === 0) return `${rbtName}: no completed sessions`;
    await timesheetService.generateTimesheetFromSessionNotes(
      company?.id || user?.companyId || '',
      rbtId,
      rbtName,
      rbtRole,
      start,
      end,
      notesInPeriod
    );
    return `${rbtName}: draft from ${notesInPeriod.length} session(s)`;
  };

  // Validate + parse the selected period. Returns null on error (sets message).
  const parsePeriod = (): { start: Date; end: Date } | null => {
    const start = new Date(`${periodStart}T00:00:00`);
    const end = new Date(`${periodEnd}T23:59:59`);
    if (start > end) {
      setError('Start date must be on or before the end date.');
      return null;
    }
    return { start, end };
  };

  const handleGenerate = async () => {
    if (!user) return;
    setGenerating(true);
    setGenerateMessage(null);
    setError(null);
    try {
      const period = parsePeriod();
      if (!period) return;
      const target = resolveTarget();
      const msg = await generateForRbt(target.id, target.name, target.role, period.start, period.end);
      setGenerateMessage(
        msg.includes('no completed sessions')
          ? 'No completed sessions found in that period — nothing to generate.'
          : `Draft timesheet created (${target.name}).`
      );
      await loadTimesheets(target.id);
    } catch (err) {
      console.error('Error generating timesheet:', err);
      setError('Failed to generate timesheet');
    } finally {
      setGenerating(false);
    }
  };

  // Managers: generate a draft for every company RBT with sessions in the period.
  const handleGenerateAll = async () => {
    if (!user) return;
    setGenerating(true);
    setGenerateMessage(null);
    setError(null);
    try {
      const period = parsePeriod();
      if (!period) return;
      const results = await Promise.all(
        rbts.map((r) => generateForRbt(r.id, r.name, 'rbt', period.start, period.end).catch((e) => `${r.name}: failed`))
      );
      const created = results.filter((r) => r.includes('draft from')).length;
      setGenerateMessage(`Generated ${created} draft timesheet(s) across ${rbts.length} RBT(s).`);
      await loadTimesheets(resolveTarget().id);
    } catch (err) {
      console.error('Error generating timesheets for all:', err);
      setError('Failed to generate timesheets');
    } finally {
      setGenerating(false);
    }
  };

  const getStatusBadge = (status: TimesheetStatus) => {
    switch (status) {
      case 'draft':
        return <span className="badge badge-warning"><FileText size={14} /> Draft</span>;
      case 'submitted':
        return <span className="badge badge-info"><Clock size={14} /> Submitted</span>;
      case 'approved':
        return <span className="badge badge-success"><CheckCircle size={14} /> Approved</span>;
      case 'rejected':
        return <span className="badge badge-error"><XCircle size={14} /> Rejected</span>;
      case 'paid':
        return <span className="badge badge-success"><DollarSign size={14} /> Paid</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatHours = (hours: number) => {
    return hours.toFixed(2);
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: 'var(--spacing-8)', textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-block',
              width: '48px',
              height: '48px',
              border: '4px solid var(--color-gray-200)',
              borderTop: '4px solid var(--color-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ marginTop: 'var(--spacing-4)', color: 'var(--color-gray-600)' }}>
            Loading timesheets...
          </p>
        </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: 'var(--spacing-8)' }}>
        {/* Header */}
        <div style={{ marginBottom: 'var(--spacing-6)' }}>
          <h1 style={{ fontSize: 'var(--font-size-3xl)', marginBottom: 'var(--spacing-2)' }}>
            My Timesheets
          </h1>
          <p style={{ color: 'var(--color-gray-600)' }}>
            View your timesheet history and status
          </p>
        </div>

        {/* Generate a draft timesheet from completed sessions in a period.
            This runs the mileage calculation between consecutive GPS check-ins. */}
        <div className="card" style={{ marginBottom: 'var(--spacing-6)' }}>
          <h3 style={{ marginBottom: 'var(--spacing-3)' }}>Generate Timesheet</h3>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-4)' }}>
            Build a draft timesheet from your completed sessions in the selected period.
            Between-session mileage is calculated from GPS check-ins.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-4)', alignItems: 'flex-end' }}>
            {isManager && (
              <div>
                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-1)' }}>
                  RBT
                </label>
                <select
                  className="input"
                  value={targetRbtId || (user?.id ?? '')}
                  onChange={(e) => setTargetRbtId(e.target.value)}
                >
                  <option value={user?.id ?? ''}>Myself ({user?.name || 'me'})</option>
                  {rbts
                    .filter((r) => r.id !== user?.id)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                </select>
              </div>
            )}
            <div>
              <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-1)' }}>
                Period start
              </label>
              <input
                type="date"
                className="input"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-1)' }}>
                Period end
              </label>
              <input
                type="date"
                className="input"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? 'Generating...' : 'Generate Timesheet'}
            </button>
            {isManager && rbts.length > 0 && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleGenerateAll}
                disabled={generating}
                title="Generate a draft timesheet for every RBT with sessions in this period"
              >
                {generating ? 'Generating...' : 'Generate for all RBTs'}
              </button>
            )}
          </div>
          {generateMessage && (
            <div className="alert alert-info" style={{ marginTop: 'var(--spacing-4)' }}>
              <CheckCircle size={20} />
              {generateMessage}
            </div>
          )}
        </div>

        {/* Non-billable time — the logged-in person's own activity clock. */}
        <div className="card" style={{ marginBottom: 'var(--spacing-6)' }}>
          <h3 style={{ marginBottom: 'var(--spacing-3)' }}>Non-billable Time</h3>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-4)' }}>
            Log time that isn't a billable client session — meetings, training, supervision,
            documentation, admin, coordination, travel, onboarding.
          </p>

          {activityMessage && (
            <div className="alert alert-info" style={{ marginBottom: 'var(--spacing-4)' }}>
              {activityMessage}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-4)', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-1)' }}>
                Category
              </label>
              <select className="input" value={activityCategory} onChange={(e) => setActivityCategory(e.target.value as ActivityCategory)}>
                {ACTIVITY_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {ACTIVITY_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-1)' }}>
                Notes (optional)
              </label>
              <input className="input" value={activityNotes} onChange={(e) => setActivityNotes(e.target.value)} />
            </div>
          </div>

          <div style={{ marginTop: 'var(--spacing-4)', display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center', flexWrap: 'wrap' }}>
            {openEntry ? (
              <>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-primary)', fontWeight: 600 }}>
                  On the clock: {ACTIVITY_CATEGORY_LABELS[openEntry.category]} (since {openEntry.startTime.toDate().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })})
                </span>
                <button type="button" className="btn btn-primary" onClick={handleClockOut} disabled={activityBusy}>
                  Clock out
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-primary" onClick={handleClockIn} disabled={activityBusy}>
                Clock in
              </button>
            )}
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>or log a block:</span>
            <input
              type="number"
              min="1"
              placeholder="minutes"
              className="input"
              style={{ width: '110px' }}
              value={activityMinutes}
              onChange={(e) => setActivityMinutes(e.target.value)}
            />
            <button type="button" className="btn btn-secondary" onClick={handleAddActivity} disabled={activityBusy}>
              Add
            </button>
          </div>

          {activityEntries.length > 0 && (
            <div style={{ marginTop: 'var(--spacing-4)' }}>
              <h4 style={{ marginBottom: 'var(--spacing-2)' }}>
                This period — {activityEntries.reduce((s, e) => s + (e.minutes || 0), 0)} min ({(activityEntries.reduce((s, e) => s + (e.minutes || 0), 0) / 60).toFixed(2)} hrs)
              </h4>
              {activityEntries.map((e) => (
                <div
                  key={e.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: 'var(--spacing-2)',
                    borderBottom: '1px solid var(--color-gray-100)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  <span>
                    {ACTIVITY_CATEGORY_LABELS[e.category]}
                    {e.notes ? ` — ${e.notes}` : ''}
                    <span style={{ color: 'var(--color-gray-500)', marginLeft: 'var(--spacing-2)' }}>
                      {e.startTime.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </span>
                  <span style={{ fontWeight: 600 }}>{e.minutes} min</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-4)' }}>
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {/* Timesheets List */}
        {timesheets.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 'var(--spacing-8)' }}>
            <Calendar size={48} style={{ color: 'var(--color-gray-400)', margin: '0 auto var(--spacing-4)' }} />
            <h3 style={{ color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-2)' }}>
              No Timesheets Yet
            </h3>
            <p style={{ color: 'var(--color-gray-500)' }}>
              Timesheets will appear here after you complete sessions.
            </p>
          </div>
        ) : (
          <div className="grid" style={{ gap: 'var(--spacing-4)' }}>
            {timesheets.map(timesheet => (
              <div
                key={timesheet.id}
                className="card"
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedTimesheet(selectedTimesheet?.id === timesheet.id ? null : timesheet)}
              >
                {/* Timesheet Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 'var(--spacing-4)' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                      <Calendar size={20} color="var(--color-primary)" />
                      <h3 style={{ fontSize: 'var(--font-size-lg)' }}>
                        {formatDate(timesheet.periodStart)} - {formatDate(timesheet.periodEnd)}
                      </h3>
                    </div>
                    {timesheet.employeeId && (
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                        Employee ID: {timesheet.employeeId}
                      </p>
                    )}
                  </div>
                  {getStatusBadge(timesheet.status)}
                </div>

                {/* Timesheet Stats */}
                <div className="grid-responsive" style={{ gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)' }}>
                  <div>
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-1)' }}>
                      Total Hours
                    </p>
                    <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: '600' }}>
                      {formatHours(timesheet.totalHours)}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-1)' }}>
                      Sessions
                    </p>
                    <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: '600' }}>
                      {timesheet.timeEntries.length}
                    </p>
                  </div>
                  {(timesheet.totalMileageReimbursement || 0) > 0 && (
                    <div>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)', marginBottom: 'var(--spacing-1)' }}>
                        Mileage
                      </p>
                      <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: '600' }}>
                        ${(timesheet.totalMileageReimbursement || 0).toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Expanded Details */}
                {selectedTimesheet?.id === timesheet.id && (
                  <div style={{ borderTop: '1px solid var(--color-gray-200)', paddingTop: 'var(--spacing-4)', marginTop: 'var(--spacing-4)' }}>
                    <h4 style={{ marginBottom: 'var(--spacing-3)' }}>Billing Code Breakdown</h4>
                    <div style={{ marginBottom: 'var(--spacing-4)' }}>
                      {Object.entries(timesheet.hoursByBillingCode).map(([code, hours]) => (
                        <div
                          key={code}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: 'var(--spacing-2)',
                            backgroundColor: 'var(--color-gray-50)',
                            borderRadius: 'var(--border-radius-sm)',
                            marginBottom: 'var(--spacing-2)',
                          }}
                        >
                          <span style={{ fontWeight: '500' }}>{code}</span>
                          <span>{formatHours(hours || 0)} hours</span>
                        </div>
                      ))}
                    </div>

                    {timesheet.status === 'rejected' && timesheet.rejectionReason && (
                      <div className="alert alert-error" style={{ marginTop: 'var(--spacing-4)' }}>
                        <XCircle size={20} />
                        <div>
                          <strong>Rejection Reason:</strong>
                          <p>{timesheet.rejectionReason}</p>
                        </div>
                      </div>
                    )}

                    {timesheet.disputeRaised && (
                      <div className="alert alert-warning" style={{ marginTop: 'var(--spacing-4)' }}>
                        <AlertCircle size={20} />
                        <div>
                          <strong>Dispute Raised</strong>
                          {timesheet.employeeNotes && <p>{timesheet.employeeNotes}</p>}
                          {timesheet.disputeResolution && (
                            <p style={{ marginTop: 'var(--spacing-2)' }}>
                              <strong>Resolution:</strong> {timesheet.disputeResolution}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    <h4 style={{ marginTop: 'var(--spacing-4)', marginBottom: 'var(--spacing-3)' }}>
                      Time Entries ({timesheet.timeEntries.length})
                    </h4>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {timesheet.timeEntries.map(entry => (
                        <div
                          key={entry.id}
                          style={{
                            padding: 'var(--spacing-3)',
                            border: '1px solid var(--color-gray-200)',
                            borderRadius: 'var(--border-radius-sm)',
                            marginBottom: 'var(--spacing-2)',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--spacing-2)' }}>
                            <span style={{ fontWeight: '500' }}>{entry.clientName || 'Client'}</span>
                            <span className="badge">{entry.billingCode}</span>
                          </div>
                          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-600)' }}>
                            <p>{formatDate(entry.sessionStartTime)} - {formatDate(entry.sessionEndTime)}</p>
                            <p>{formatHours(entry.billableMinutes / 60)} hours</p>
                            {entry.locationVerified && (
                              <p style={{ color: 'var(--color-success)' }}>Location verified</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
};
